import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import axios from 'axios'

import { useBundleStore } from './bundle'
import { showBundleSummary } from '@/utils/bundle'
import { isSubscriptionItem, isNonBundleItem, sumQuantity } from '@/utils'
import { getTemplateData } from '@/utils/dom'
import { useFueled } from '@/services/fueled'
import { CART_ITEM_ADD, track } from '@/services/events'
import { useAmplitude } from '@/services/amplitude'

import type { Product, Variant } from '@/types/shopify'
import type { Cart, CartItem, CartUpdatePayload, SimpleCustomer } from '@/types/shopify/cart'

type LoadingState = 'checkout' | 'update' | 'cart' | 'add' | undefined

const client = axios.create()

export const useCartStore = defineStore('cart', () => {
  const bundleStore = useBundleStore()
  const { trackAddToCart } = useFueled()
  const { trackCart } = useAmplitude()
  const loading = ref<LoadingState>()
  const cart = ref<Cart>()
  const customer = ref(getTemplateData<SimpleCustomer>('#json-customer'))

  const items = computed(() => cart.value?.items || [])
  const subscriptionItems = computed(() => items.value.filter(isSubscriptionItem))
  const nonBundleItems = computed(() => items.value.filter(isNonBundleItem))
  const nonBundleItemsPrice = computed(() => nonBundleItems.value.reduce((acc, item) => acc + (item.price * item.quantity), 0))

  const isFirstOrder = computed(() => (customer.value?.order_count || 0) === 0)
  const isSubscriptionOrder = computed(() => subscriptionItems.value.length)
  const subExCount = computed(() => sumQuantity(subscriptionItems.value))
  const displayNotification = computed(() => Boolean(nonBundleItems.value.length || bundleStore.totalQuantity))

  const totalPrice = computed(() => bundleStore.priceCents + nonBundleItemsPrice.value)
  const subscriptionTotalPrice = computed(() => bundleStore.pricing.minCents + nonBundleItemsPrice.value)
  const onetimeTotalPrice = computed(() => bundleStore.pricing.onetimeCents + nonBundleItemsPrice.value)

  const cartItemsAnalyticsData = computed(() => items.value.map((item: CartItem) => ({
    sku: item.sku,
    quantity: item.quantity
  })))

  const load = async () => {
    loading.value = 'cart'
    const { data } = await client.get('/cart.json')
    cart.value = data
    loading.value = undefined
  }

  const update = async (payload: CartUpdatePayload) => {
    loading.value = 'update'
    const { data } = await client.post('/cart/update.json', payload)
    cart.value = data
    loading.value = undefined
  }

  const add = async (product?: Product, variant?: Variant | CartItem, properties?: Record<string, string | number>) => {
    if (!(product && variant)) {
      return
    }
    loading.value = 'add'
    await Promise.all([
      client.post('/cart/add.json', { id: variant.id, properties }),
      trackAddToCart(product, variant, 1)
    ])
    await load()
    if (cart.value) {
      track(CART_ITEM_ADD, cart.value)
      await trackCart(cart.value)
    }
    loading.value = undefined
    showBundleSummary()
  }

  return {
    load,
    update,
    add,
    cart,
    customer,
    items,
    subscriptionItems,
    nonBundleItems,
    loading,
    isFirstOrder,
    isSubscriptionOrder,
    subExCount,
    nonBundleItemsPrice,
    totalPrice,
    subscriptionTotalPrice,
    onetimeTotalPrice,
    displayNotification,
    cartItemsAnalyticsData
  }
})
